import { XMarkIcon } from "@heroicons/react/24/outline";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { useSendUserProfilePinWhatsappAPI } from "@/app/hooks/api_hooks/PWL/useSendUserProfilePinWhatsappAPI";

const MOBILE_NUMBER_LENGTH = 10;

/* Keeps the field at a 10 digit Indian mobile. Digits typed past the tenth are ignored, while a
   pasted number carrying a country/trunk prefix ("+91 ...", "091...", "0...") drops the prefix
   instead of getting truncated from the front, which would silently change the number. */
const toMobileNumberDigits = (rawValue: string): string => {
  const digits = rawValue.replace(/\D/g, "");
  const withoutPrefix = digits.length > MOBILE_NUMBER_LENGTH ? digits.replace(/^(?:091|91|0)/, "") : digits;
  return withoutPrefix.slice(0, MOBILE_NUMBER_LENGTH);
};

export interface ISendPinModalProps {
  open: boolean;
  setOpen: (e: boolean) => void;
  userId: string;
  userMobileNumber: string;
  onSuccess?: () => void;
  bookingId?: string;
  clinicId?: string;
  intent?: string;
}

const SendPinModal: React.FC<ISendPinModalProps> = ({
  open,
  setOpen,
  userId,
  userMobileNumber,
  onSuccess,
  bookingId,
  clinicId,
  intent,
}) => {
  const { execute: sendPin } = useSendUserProfilePinWhatsappAPI();
  const [isLoading, setIsLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(toMobileNumberDigits(userMobileNumber || ""));

  useEffect(() => {
    if (open) {
      setPhoneNumber(toMobileNumberDigits(userMobileNumber || ""));
      setIsLoading(false);
    } else {
      // Clear state when modal closes
      setPhoneNumber("");
      setIsLoading(false);
    }
  }, [open, userMobileNumber]);

  const isPhoneValid = phoneNumber.length === MOBILE_NUMBER_LENGTH;

  const handleSendPin = async () => {
    if (!userId) {
      toast.error("User ID is missing");
      return;
    }

    if (!phoneNumber) {
      toast.error("Please enter a phone number");
      return;
    }

    if (!isPhoneValid) {
      toast.error(`Phone number must be exactly ${MOBILE_NUMBER_LENGTH} digits`);
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        user_id: userId,
        user_mobile_number: phoneNumber,
        created_by: "CM",
        source: "VMS",
        booking_id: bookingId,
        clinic_id: clinicId,
        intent: intent,
      };
      const response = await sendPin(payload);

      setIsLoading(false);

      if (response?.status) {
        toast.success(response?.message ?? "PIN sent successfully");
        setOpen(false);
        onSuccess?.();
      } else {
        toast.error(response?.message ?? "Failed to send PIN");
      }
    } catch (error) {
      setIsLoading(false);
      toast.error("Error calling API");
    }
  };

  if (!open) return null;

  const modalContent = (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      {/* Overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1,
        }}
        onClick={() => {
          setOpen(false);
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "relative",
          zIndex: 2,
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
          maxWidth: "450px",
          width: "100%",
          padding: "24px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "600", color: "black" }}>Send PIN via WhatsApp</h3>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
            }}
            style={{
              backgroundColor: "rgba(128, 141, 160, 0.1)",
              borderRadius: "9999px",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "none",
              padding: 0,
            }}
          >
            <XMarkIcon style={{ width: "16px", height: "16px" }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ backgroundColor: "#F5F5F5", borderRadius: "10px", padding: "16px" }}>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "500", color: "#666666", marginBottom: "8px" }}>
                Phone Number
              </label>
              <input
                type="tel"
                value={phoneNumber}
                disabled
                readOnly
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid #D9DEE7",
                  backgroundColor: "#EDEDED",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#111111",
                  outline: "none",
                  cursor: "not-allowed",
                  WebkitTextFillColor: "#111111",
                  opacity: 1,
                }}
              />
              <p style={{ fontSize: "11px", color: "#999999", marginTop: "4px" }}>
                {phoneNumber.length}/{MOBILE_NUMBER_LENGTH} digits {isPhoneValid ? "✅" : ""}
              </p>
            </div>
          </div>

          <p style={{ fontSize: "12px", color: "#666666", textAlign: "center" }}>
            A 4-digit PIN will be sent to <strong>{phoneNumber || "this number"}</strong> via WhatsApp
          </p>
        </div>

        {/* Buttons */}
        <div style={{ marginTop: "24px", display: "flex", gap: "12px" }}>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
            }}
            style={{
              flex: 1,
              height: "44px",
              borderRadius: "8px",
              border: "1px solid #D9DEE7",
              backgroundColor: "white",
              padding: "0 16px",
              fontSize: "13px",
              fontWeight: "600",
              color: "#111111",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = "#F4F4F4";
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = "white";
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              handleSendPin();
            }}
            disabled={isLoading || !isPhoneValid}
            style={{
              flex: 1,
              height: "44px",
              borderRadius: "8px",
              padding: "0 16px",
              fontSize: "13px",
              fontWeight: "600",
              color: "white",
              cursor: isLoading || !isPhoneValid ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              backgroundColor: isLoading || !isPhoneValid ? "#B9B9B9" : "#111111",
              border: "none",
            }}
            onMouseEnter={(e) => {
              if (!isLoading && isPhoneValid) {
                (e.target as HTMLButtonElement).style.opacity = "0.95";
              }
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLButtonElement).style.opacity = "1";
            }}
          >
            {isLoading ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SendPinModal;
